import React, {useEffect, useState} from 'react';
import './AddActivityModal.css';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import axios from 'axios';
import {USER_ENDPOINTS} from '../../Constants';
import EventOverlapService from '../../services/EventOverlapService';
import OverlapWarningModal from '../overlapWarningModal/OverlapWarningModal';
import {DesktopTimePicker} from '@mui/x-date-pickers/DesktopTimePicker';

function AddActivityModal({isOpen, onClose, onSave, selectedDate, eventToEdit}) {
    const [activity, setActivity] = useState('');
    const [startTime, setStartTime] = useState(dayjs().hour(16).minute(0));
    const [endTime, setEndTime] = useState(dayjs().hour(17).minute(0));
    const [color, setColor] = useState('#808080'); // Default gray for personal events
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [timeError, setTimeError] = useState(''); // New state for time validation errors
    const [editMode, setEditMode] = useState(false);
    const [eventId, setEventId] = useState(null);
    const [eventDate, setEventDate] = useState(null);
    const [showOverlapWarning, setShowOverlapWarning] = useState(false);
    const [overlappingEvents, setOverlappingEvents] = useState([]);
    const [pendingEventData, setPendingEventData] = useState(null);

    // Initialize form with data if we're editing an existing event
    useEffect(() => {
        if (eventToEdit) {
            setActivity(eventToEdit.title || '');

            // Parse time strings to dayjs objects
            if (eventToEdit.start) {
                const [hours, minutes] = eventToEdit.start.split(':').map(Number);
                setStartTime(dayjs().hour(hours).minute(minutes));
            }

            if (eventToEdit.end) {
                const [hours, minutes] = eventToEdit.end.split(':').map(Number);
                setEndTime(dayjs().hour(hours).minute(minutes));
            }

            // Use the event's color or default gray for personal events
            setColor(eventToEdit.color || '#808080');
            setEditMode(true);
            setEventId(eventToEdit.id || null);

            // If the event has a scheduled date, use it, otherwise use selectedDate
            if (eventToEdit.scheduledDate) {
                setEventDate(new Date(eventToEdit.scheduledDate));
            } else {
                setEventDate(selectedDate || new Date());
            }
        } else {
            // Reset form for new activity
            setActivity('');
            setStartTime(dayjs().hour(16).minute(0));
            setEndTime(dayjs().hour(17).minute(0));
            setColor('#808080'); // Default gray for new personal events
            setEditMode(false);
            setEventId(null);
            setEventDate(selectedDate || new Date());
        }
        // Clear any previous errors when modal opens/changes
        setError('');
        setTimeError('');
    }, [eventToEdit, selectedDate, isOpen]);

    // Function to validate time range
    const validateTimeRange = (newStartTime, newEndTime) => {
        if (!newStartTime || !newEndTime) {
            return null; // Don't validate if either time is null/undefined
        }

        if (newStartTime.isAfter(newEndTime) || newStartTime.isSame(newEndTime)) {
            return 'Ora de început trebuie să fie mai mică decât ora de sfârșit.';
        }

        return null; // No error
    };

    // Function to check if the selected date is in the past
    const isPastDate = (date) => {
        if (!date) return false;

        const today = new Date();
        const selectedDay = new Date(date);

        // Set both dates to start of day for comparison
        today.setHours(0, 0, 0, 0);
        selectedDay.setHours(0, 0, 0, 0);

        return selectedDay < today;
    };

    const handleStartTimeChange = (newTime) => {
        const validTime = newTime || dayjs().hour(16).minute(0);
        setStartTime(validTime);

        // Validate time range
        const timeValidationError = validateTimeRange(validTime, endTime);
        setTimeError(timeValidationError || '');

        // Auto-adjust end time if start time is after current end time
        if (validTime.isAfter(endTime) || validTime.isSame(endTime)) {
            const newEndTime = validTime.add(15, 'minute');
            setEndTime(newEndTime);
            // Re-validate with the new end time
            const newTimeValidationError = validateTimeRange(validTime, newEndTime);
            setTimeError(newTimeValidationError || '');
        }
    };

    const handleEndTimeChange = (newTime) => {
        const validTime = newTime || dayjs().hour(17).minute(0);
        setEndTime(validTime);

        // Validate time range
        const timeValidationError = validateTimeRange(startTime, validTime);
        setTimeError(timeValidationError || '');

        // Auto-adjust start time if end time is before current start time
        if (validTime.isBefore(startTime) || validTime.isSame(startTime)) {
            const newStartTime = validTime.subtract(15, 'minute');
            setStartTime(newStartTime);
            // Re-validate with the new start time
            const newTimeValidationError = validateTimeRange(newStartTime, validTime);
            setTimeError(newTimeValidationError || '');
        }
    };

    const handleSave = async () => {
        // Clear previous errors
        setError('');
        setTimeError('');

        // Validate that we have a description
        if (!activity.trim()) {
            setError('Te rugăm să completezi descrierea activității.');
            return;
        }

        // Check if trying to save event in the past (only for new events)
        if (!editMode && isPastDate(eventDate)) {
            setError('Nu poți crea evenimente în trecut. Te rugăm să selectezi o dată din viitor.');
            return;
        }

        // Validate time range
        const timeValidationError = validateTimeRange(startTime, endTime);
        if (timeValidationError) {
            setTimeError(timeValidationError);
            return;
        }

        setIsSubmitting(true);

        try {
            // Fix for timezone issues: create date with day, month, year components
            // to prevent day shifts due to timezone conversion
            const year = eventDate.getFullYear();
            const month = eventDate.getMonth() + 1; // getMonth() is 0-indexed
            const day = eventDate.getDate();

            // Format date as YYYY-MM-DD with proper padding
            const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

            // Format the times for the API (HH:mm:ss)
            const formattedStartTime = startTime.format('HH:mm:00');
            const formattedEndTime = endTime.format('HH:mm:00');

            console.log(`${editMode ? 'Updating' : 'Creating'} event for date:`, formattedDate); // Debug log

            // Check for overlapping events before saving (only for new events or if time/date changed)
            const shouldCheckOverlap = !editMode ||
                (editMode && (
                    formattedDate !== eventToEdit?.scheduledDate ||
                    startTime.format('HH:mm') !== eventToEdit?.start ||
                    endTime.format('HH:mm') !== eventToEdit?.end
                ));

            if (shouldCheckOverlap) {
                const overlaps = await EventOverlapService.checkForOverlaps(
                    formattedDate,
                    startTime.format('HH:mm'),
                    endTime.format('HH:mm'),
                    editMode ? eventId : null
                );

                if (overlaps.length > 0) {
                    // Store the event data to save later if user confirms
                    setPendingEventData({
                        description: activity,
                        scheduledDate: formattedDate,
                        startTime: formattedStartTime,
                        endTime: formattedEndTime
                    });
                    setOverlappingEvents(overlaps);
                    setShowOverlapWarning(true);
                    setIsSubmitting(false);
                    return;
                }
            }

            // If no overlaps or user confirmed, proceed with saving
            await saveEvent({
                description: activity,
                scheduledDate: formattedDate,
                startTime: formattedStartTime,
                endTime: formattedEndTime
            });

        } catch (err) {
            console.error(`Error ${editMode ? 'updating' : 'saving'} activity:`, err);
            setError(`A apărut o eroare la ${editMode ? 'actualizarea' : 'salvarea'} activității. Te rugăm să încerci din nou.`);
            setIsSubmitting(false);
        }
    };

    const saveEvent = async (eventData) => {
        try {
            // Get token from localStorage
            const token = localStorage.getItem('token');
            eventData.color = color;
            let response;

            if (editMode && eventId) {
                // Update existing event
                eventData.id = eventId; // Add ID for update
                response = await axios.put(USER_ENDPOINTS.UPDATE_EVENT, eventData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } else {
                // Create new event
                response = await axios.post(USER_ENDPOINTS.CREATE_PERSONAL_EVENT, eventData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            // Create the calendar event object for UI with the selected color
            const calendarEvent = {
                title: activity,
                start: startTime.format('HH:mm'),
                end: endTime.format('HH:mm'),
                color: color // Use the selected color for personal events
            };

            // Call the onSave function to update the UI
            onSave(calendarEvent);

            // Close the modal and reset form
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlapConfirm = async () => {
        setShowOverlapWarning(false);
        if (pendingEventData) {
            setIsSubmitting(true);
            await saveEvent(pendingEventData);
            setPendingEventData(null);
        }
    };

    const handleOverlapCancel = () => {
        setShowOverlapWarning(false);
        setPendingEventData(null);
        setIsSubmitting(false);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header" style={{flexDirection: "row-reverse"}}>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="form-group" style={{flexWrap: "nowrap"}}>
                        <label>Activitate</label>
                        <textarea
                            value={activity}
                            onChange={(e) => setActivity(e.target.value)}
                            placeholder="Descriere activitate"
                        />
                    </div>

                    <div className="form-group" style={{flexWrap: "nowrap"}}>
                        <label>Ora început</label>
                        <div className="time-input">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopTimePicker
                                    value={startTime}
                                    onChange={handleStartTimeChange}
                                    format="HH:mm"
                                    ampm={false}
                                    sx={{width: '100%'}}
                                />
                            </LocalizationProvider>
                        </div>
                    </div>

                    <div className="form-group" style={{flexWrap: "nowrap"}}>
                        <label>Ora sfârșit</label>
                        <div className="time-input">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopTimePicker
                                    value={endTime}
                                    onChange={handleEndTimeChange}
                                    format="HH:mm"
                                    ampm={false}
                                    sx={{width: '100%'}}
                                />
                            </LocalizationProvider>
                        </div>
                    </div>

                    <div className="form-group" style={{flexWrap: "nowrap"}}>
                        <label>Culoare</label>
                        <div className="color-picker-container">
                            <input
                                type="color"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                className="color-picker-input"
                                title="Selectează culoarea evenimentului"
                            />
                            <div
                                className="color-preview"
                                style={{backgroundColor: color}}
                                title={`Culoare selectată: ${color}`}
                            ></div>
                        </div>
                    </div>

                    {/* Display time validation error */}
                    {timeError && (
                        <div className="error-message" style={{color: 'red', margin: '10px 0', fontSize: '14px'}}>
                            {timeError}
                        </div>
                    )}

                    {/* Display general error */}
                    {error && (
                        <div className="error-message" style={{color: 'red', margin: '10px 0'}}>
                            {error}
                        </div>
                    )}

                    {/* Warning for past date when editing */}
                    {editMode && isPastDate(eventDate) && (
                        <div className="warning-message"
                             style={{color: '#f39c12', margin: '10px 0', fontSize: '14px', fontStyle: 'italic'}}>
                            ⚠️ Acest eveniment este programat în trecut.
                        </div>
                    )}
                </div>
                <div className="modal-footer" style={{margin: '0rem 1rem 1rem 1rem'}}>
                    <button className="cancel-button" onClick={onClose}>Anulează</button>
                    <button
                        className="save-button"
                        onClick={handleSave}
                        disabled={isSubmitting || !!timeError}
                    >
                        {isSubmitting ? 'Se salvează...' : (editMode ? 'Actualizează' : 'Adaugă')}
                    </button>
                </div>
            </div>
            <OverlapWarningModal
                isOpen={showOverlapWarning}
                onClose={handleOverlapCancel}
                onConfirm={handleOverlapConfirm}
                overlappingEvents={overlappingEvents}
                newEventDetails={{
                    title: activity,
                    startTime: startTime.format('HH:mm'),
                    endTime: endTime.format('HH:mm'),
                    date: eventDate ? eventDate.toLocaleDateString('ro-RO') : ''
                }}
            />
        </div>
    );
}

export default AddActivityModal;